import { AutoCompleteOption } from "../components/form/APAutoComplete";

export interface QuoteParameters {
  makeId?: string | null;
  modelId?: string | null;
  modelYear?: string | null;
  variantId?: string | null;
  purchaseType?: string | null;
  term?: string | null;
  salary?: string | null;
  businessUsage?: string | null;
  discount?: string | null;
  listPrice?: string | null;
  overwriteQuote?: string | null;
  aiQueryId?: string | null;
  vehicleReviewAIQueryId?: string | null;
  vehicleSearchEventId?: string | null;
  vehicleComparisonViewId?: string | null;
  isCreateUrl?: string | null;
}

export const QuoteParameterOptions: AutoCompleteOption[] = [
  { label: "Make", id: "makeId" },
  { label: "Model", id: "modelId" },
  { label: "Model Year", id: "modelYear" },
  { label: "Variant", id: "variantId" },
  { label: "Purchase Type", id: "purchaseType" },
  { label: "Term", id: "term" },
  { label: "Salary", id: "salary" },
  { label: "Business Usage", id: "businessUsage" },
  { label: "Discount", id: "discount" },
  { label: "List Price", id: "listPrice" },
];

/**
 * Extracts quote parameters from URLSearchParams
 */
export const extractQuoteParameters = (
  searchParams: URLSearchParams
): QuoteParameters => {
  return {
    makeId: searchParams.get("makeId"),
    modelId: searchParams.get("modelId"),
    modelYear: searchParams.get("modelYear"),
    variantId: searchParams.get("variantId"),
    purchaseType: searchParams.get("purchaseType"),
    term: searchParams.get("term"),
    salary: searchParams.get("salary"),
    businessUsage: searchParams.get("businessUsage"),
    discount: searchParams.get("discount"),
    listPrice: searchParams.get("listPrice"),
    overwriteQuote: searchParams.get("overwriteQuote"),
    aiQueryId: searchParams.get("aiQueryId"),
    vehicleReviewAIQueryId: searchParams.get("vehicleReviewAIQueryId"),
    vehicleSearchEventId: searchParams.get("vehicleSearchEventId"),
    vehicleComparisonViewId: searchParams.get("vehicleComparisonViewId"),
    isCreateUrl: searchParams.get("isCreateUrl"),
  };
};

/**
 * Builds query string from quote parameters
 */
export const buildQuoteQueryString = (params: QuoteParameters): string => {
  const queryParams = new URLSearchParams();

  // Add vehicle parameters if provided (only IDs, names are resolved from data)
  if (params.makeId) queryParams.set("makeId", params.makeId);
  if (params.modelId) queryParams.set("modelId", params.modelId);
  if (params.modelYear) queryParams.set("modelYear", params.modelYear);
  if (params.variantId) queryParams.set("variantId", params.variantId);
  if (params.purchaseType) queryParams.set("purchaseType", params.purchaseType);

  // Add other parameters if provided
  if (params.term) queryParams.set("term", params.term);
  if (params.salary) queryParams.set("salary", params.salary);
  if (params.businessUsage)
    queryParams.set("businessUsage", params.businessUsage);
  if (params.discount) queryParams.set("discount", params.discount);
  if (params.listPrice) queryParams.set("listPrice", params.listPrice);
  if (params.overwriteQuote)
    queryParams.set("overwriteQuote", params.overwriteQuote);
  if (params.aiQueryId) queryParams.set("aiQueryId", params.aiQueryId);
  if (params.vehicleReviewAIQueryId)
    queryParams.set("vehicleReviewAIQueryId", params.vehicleReviewAIQueryId);
  if (params.vehicleSearchEventId)
    queryParams.set("vehicleSearchEventId", params.vehicleSearchEventId);
  if (params.vehicleComparisonViewId)
    queryParams.set("vehicleComparisonViewId", params.vehicleComparisonViewId);
  if (params.isCreateUrl) queryParams.set("isCreateUrl", params.isCreateUrl);

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : "";
};

/**
 * Checks if any vehicle parameters are provided
 */
export const hasVehicleParameters = (params: QuoteParameters): boolean => {
  return !!(
    params.makeId ||
    params.modelId ||
    params.modelYear ||
    params.variantId
  );
};

/**
 * Merges quote parameters, with newParams taking precedence over existingParams
 */
export const mergeQuoteParameters = (
  existingParams: QuoteParameters,
  newParams: Partial<QuoteParameters>
): QuoteParameters => {
  return {
    ...existingParams,
    ...newParams,
  };
};
